import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18N_MANUAL_SELECTION_KEY } from '../../constants/i18n';
import { useAuthStore } from '../../store/authStore';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import ScoutMindLogo from '../../components/Logo/ScoutMindLogo';
import ContactFormModal from '../../components/ContactForm/ContactFormModal';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import toast from 'react-hot-toast';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Play
} from 'lucide-react';

const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuthStore();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  // Check for language parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const langParam = params.get('lang');
    
    if (langParam && (langParam === 'es' || langParam === 'en')) {
      if (i18n.language !== langParam) {
        i18n.changeLanguage(langParam);
        // Mark as manual selection when URL parameter is used
        localStorage.setItem(I18N_MANUAL_SELECTION_KEY, 'true');
      }
    }
  }, [location.search, i18n]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      toast.error(t('auth.enterCredentials'));
      return;
    }

    try {
      await login({
        username: formData.username,
        password: formData.password
      });
      toast.success(t('auth.loginSuccess'));
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.message || t('auth.loginError'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative" style={{
      background: 'linear-gradient(180deg, #000000 0%, #0F2A7D 50%, #12182E 100%)'
    }}>
      {/* Overlay sutil para profundidad y textura */}
      <div className="absolute inset-0 bg-black/10"></div>
      
      {/* Efectos de partículas sutiles con colores del logo */}
      <div className="absolute inset-0 opacity-15">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-pulse"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-200 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-1/3 right-1/4 w-1 h-1 bg-blue-100 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-1/2 left-1/5 w-1 h-1 bg-white/70 rounded-full animate-pulse" style={{ animationDelay: '3s' }}></div>
        <div className="absolute top-3/4 right-1/5 w-1.5 h-1.5 bg-blue-200/80 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>
      <div className="relative w-full max-w-md">
        {/* Language Selector */}
        <div className="flex justify-end mb-4">
          <LanguageSelector variant="compact" className="text-white" />
        </div>

        {/* Login Card */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <ScoutMindLogo variant="full" size="lg" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {t('auth.welcomeMessage')}
            </h1>
            <p className="text-blue-100">
              {t('auth.enterCredentials')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t('auth.username')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                <input
                  type="text"
                  autoComplete="username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                  placeholder={t('auth.username')}
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="w-full pl-11 pr-11 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                  placeholder={t('auth.password')}
                  required
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-blue-200 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 backdrop-blur-sm">
                <p className="text-sm text-red-200">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-6 text-white font-semibold rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 focus:ring-4 focus:outline-none flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              style={{ background: '#0F2A7D' }}
              onMouseEnter={(e) => !isLoading && (e.currentTarget.style.background = '#0A1F5C')}
              onMouseLeave={(e) => e.currentTarget.style.background = '#0F2A7D'}
            >
              {isLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <span>{t('auth.login')}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Forgot Password Link */}
          <div className="mt-6 text-center">
            <Link
              to="/forgot-password"
              className="text-blue-300 hover:text-blue-200 text-sm transition-colors"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
          <p className="text-blue-200 text-sm mb-4">
            {t('auth.noAccount')}
          </p>
          <button
            onClick={() => setIsContactModalOpen(true)}
            className="px-6 py-2 bg-gradient-scoutmind-medium hover:bg-gradient-scoutmind-light text-white font-semibold rounded-l shadow-xl transition-all duration-300 transform hover:scale-105 focus:ring-4 focus:ring-scoutmind-primary/50 focus:outline-none inline-flex items-center justify-center space-x-2 mx-auto"
          >
            <Play className="w-5 h-5" />
            <span>{i18n.language === 'en' ? 'Start your trial' : 'Comenzar tu prueba'}</span>
          </button>
        </div>

        </div>

        {/* App Info */}
        <div className="mt-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">
            {t('app.name')}
          </h2>
          <p className="text-blue-200 text-sm">
            {t('app.description')}
          </p>
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </div>
  );
};

export default LoginPage;
